import React, { useEffect, useRef, FC } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import BreakpointsConstants from '../../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { appendToUrlPath } from '../../../../../utils/link.utils';
import Picture from '../../../picture';
import SolutionBadge from './solutionBadge';
import { modelSolution } from '../../../../../constants/hash-route.constants';
import RoutableNotEditableLink from '../../../routableNotEditableLink';
import { TProductCard } from '../../types/seriesListing.types';
import SummarySpecs from './summarySpecs';
import '../../../../../styles/@shared/product-card.scss';
import MsrpBlock from './msrpBlock';
import CtaBlock from './ctaBlock';
import './styles.scss';
import { appendRootToRichTextInternalLinks } from '../../../../../utils/appendRootToRichTextInternalLinks.utils';

const ProductCard: FC<WithTranslation & TProductCard> = (props) => {
  const {
    titleUrl,
    title,
    eyebrow,
    description,
    summarySpecs,
    thumbnailAlt,
    baseUrl,
    bodyTextVisibility,
    msrp,
    shouldAnimate,
    animationDelay,
    onclick,
    assets,
    heroAsset
  } = props;
  const productCardRef = useRef<HTMLDivElement | null>(null);
  const buttonWrapperRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useMediaQuery(`(min-width : ${BreakpointsConstants.dsk}px)`);
  const imageProps = heroAsset && Object.keys(heroAsset).length > 0 ? heroAsset : (assets && assets[0]);

  let isAnimated = false;

  useEffect(() => {
    const handleMouseOver = (): void => {
      if (!productCardRef.current) return;
      productCardRef.current.style.height = 'calc(100% + 8px)';
      productCardRef.current.style.zIndex = '5';
      productCardRef.current.style.transition = 'height 0.3s ease-out';
    };

    const handlMouseLeave = (): void => {
      if (!productCardRef.current) return;
      productCardRef.current.style.height = 'calc(100%)';
      productCardRef.current.style.zIndex = '1';
      productCardRef.current.style.transition = 'height 0.3s ease-out';
    };
    if (isDesktop) {
      productCardRef.current?.addEventListener('mouseover', handleMouseOver);
      productCardRef.current?.addEventListener('mouseleave', handlMouseLeave);
    }
    return (): void => {
      //eslint-disable-next-line react-hooks/exhaustive-deps
      productCardRef.current?.removeEventListener('mouseover', handleMouseOver);
      //eslint-disable-next-line react-hooks/exhaustive-deps
      productCardRef.current?.removeEventListener('mouseleave', handlMouseLeave);
    };
  }, [isDesktop]);

  const animateCard = () => {
    if (isAnimated) return;

    if (productCardRef.current && shouldAnimate) {
      isAnimated = true;
      productCardRef.current?.classList.remove('product-card--animate');
      requestAnimationFrame(() => {
        // to start animation in the next re-paint cycle
        productCardRef.current?.classList.add('product-card--animate');
      });
    }
  };

  useEffect(() => animateCard(), []); // to animate on first load
  {
    !isAnimated && animateCard();
  } // to animate on re-render, but not on first load

  return (
    <article className={`product-card__wrapper ${encodeURI(eyebrow || '')}`}>
      <RoutableNotEditableLink
        href={appendToUrlPath(baseUrl || '', titleUrl || '')}
        onClick={(e): void => {
          onclick && onclick();
          !baseUrl && e.preventDefault();
        }}
      >
        <div
          className={`product-card ${shouldAnimate ? 'product-card--hide' : ''}`} //--hide to hide before animation
          ref={productCardRef}
          {...(shouldAnimate ? { style: { animationDelay } } : {})}
        >
          <div className="product-card__image-wrapper">
            {props?.solutions && props?.solutions?.length > 0 && baseUrl && (
              <SolutionBadge {...props} linkUrl={appendToUrlPath(baseUrl || '', (titleUrl || '') + '#' + modelSolution)} />
            )}
               <Picture
               productImageProps={{
                 desktopRendition: imageProps?.rendition_desktop_size500,
                 mobileRendition: imageProps?.rendition_mobile_size500,
                 imageClassName: 'product-card__image',
                 alt: thumbnailAlt,
               }}
             />
          </div>
          <div className="product-card__content">
            {title ? <h2 className="product-card__title">{title}</h2> : null}
            <div className="product-card__content-msrp-wrapper">
              <MsrpBlock msrp={msrp} />
            </div>
            {bodyTextVisibility === 'show' && description && (
              <RichText
                field={{ value: appendRootToRichTextInternalLinks(description) }}
                editable={false}
                class="product-card__description rich-text"
              />
            )}
            {summarySpecs?.length ? <SummarySpecs summarySpecs={summarySpecs} /> : null}
            <div className="product-card__button-wrapper" ref={buttonWrapperRef}>
              <CtaBlock titleUrl={titleUrl} baseUrl={baseUrl} />
            </div>
          </div>
        </div>
      </RoutableNotEditableLink>
    </article>
  );
};

export default withTranslation()(ProductCard);
